import React, { useState } from 'react';
import Button from 'shared/components/Button';
import Input from 'shared/components/Input';
import AuthConnect from '../components/AuthConnect';
import i18n from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { Auth } from 'aws-amplify';
import { getError } from 'utils/appHelpers';
import { useAlert } from 'react-alert';
import CheckBox from 'shared/components/CheckBox';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { sendEvent } from 'utils/analyticsHelpers';
import { User, Mail, Lock } from 'lucide-react';
import { validateName, validateEmail, signupRateLimiter } from 'utils/inputSanitization';
import { URLS } from 'configs';
import './Auth.scss';

const SignUp = () => {
  const alert = useAlert();
  const { t } = useTranslation();
  const [error, setError] = useState();
  const [fetch, setFetch] = useState(false);
  const [form, setForm] = useState({
    name: '',
    username: '',
    password: '',
    rpassword: '',
    policy: false,
  });
  const [validationErrors, setValidationErrors] = useState({
    name: null,
    username: null,
  });

  const onFormChange = ({ target }) => {
    const { name, value, checked, type } = target;
    const result = type === 'checkbox' ? checked : value;

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: null });
    }

    setForm({ ...form, [name]: result });
  };

  const onFieldBlur = ({ target }) => {
    const { name, value } = target;

    if (name === 'name') {
      const validation = validateName(value);
      if (!validation.isValid) {
        setValidationErrors({ ...validationErrors, name: validation.error });
      } else if (validation.sanitized !== value) {
        // Update form with sanitized value
        setForm({ ...form, name: validation.sanitized });
      }
    }

    if (name === 'username') {
      const validation = validateEmail(value);
      if (!validation.isValid) {
        setValidationErrors({ ...validationErrors, username: validation.error });
      } else if (validation.sanitized !== value) {
        setForm({ ...form, username: validation.sanitized });
      }
    }
  };

  const onFormSubmit = async e => {
    e.preventDefault();
    setFetch(true);
    setError();

    try {
      // Rate limiting check
      if (!signupRateLimiter.isAllowed()) {
        const remainingTime = Math.ceil(signupRateLimiter.getRemainingTime() / 1000);
        throw new Error(`Too many signup attempts. Please wait ${remainingTime} seconds.`);
      }

      // Validate and sanitize name
      const nameValidation = validateName(form.name);
      if (!nameValidation.isValid) {
        setValidationErrors({ ...validationErrors, name: nameValidation.error });
        setFetch(false);
        return;
      }

      // Validate and sanitize email
      const emailValidation = validateEmail(form.username);
      if (!emailValidation.isValid) {
        setValidationErrors({ ...validationErrors, username: emailValidation.error });
        setFetch(false);
        return;
      }

      // Use sanitized values
      const { password } = form;
      const username = emailValidation.sanitized;
      const name = nameValidation.sanitized;

      // Log sanitization for security monitoring (development only)
      if (process.env.NODE_ENV === 'development') {
        if (name !== form.name || username !== form.username) {
          console.warn('[SECURITY] Input sanitized during signup:', {
            originalName: form.name,
            sanitizedName: name,
            originalEmail: form.username,
            sanitizedEmail: username,
          });
        }
      }

      const attributes = { name, locale: i18n.language };
      await Auth.signUp({ username, password, attributes });
      sendEvent('conversion_event_signup', { method: 'email' });
      await Auth.signIn(username, password);
      Auth.verifyCurrentUserAttribute('email');
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        alert.error(getError(err));
        window.location.assign(URLS.CONTACT);
      } else {
        setError(getError(err));
        setFetch(false);
      }
    }
  };

  const passNoMatch = form.rpassword && form.password !== form.rpassword;

  return (
    <>
      <div className='auth-welcome'>
        <h1 className='auth-welcome__title'>{t('signup_welcome_title', 'Create Your Account')}</h1>
        <p className='auth-welcome__subtitle'>{t('signup_welcome_subtitle', 'Join thousands of smart shoppers')}</p>
      </div>

      <div className='auth-card'>
        <form onSubmit={onFormSubmit}>
          <div className='auth-form-group'>
            <label className='auth-label'>
              <User className='auth-icon' size={16} />
              {t('ph_name', 'Name')} *
            </label>
            <Input
              type='text'
              name='name'
              onChange={onFormChange}
              onBlur={onFieldBlur}
              value={form.name}
              placeholder={t('signup_name_placeholder', 'Your full name')}
              errorText={validationErrors.name}
              required
              className='mb-0'
              inputClassName='auth-input-field'
            />
          </div>
          <div className='auth-form-group'>
            <label className='auth-label'>
              <Mail className='auth-icon' size={16} />
              {t('ph_username', 'Email')} *
            </label>
            <Input
              type='text'
              name='username'
              onChange={onFormChange}
              onBlur={onFieldBlur}
              value={form.username}
              placeholder={t('signup_email_placeholder', 'your@email.com')}
              errorText={validationErrors.username}
              required
              className='mb-0'
              inputClassName='auth-input-field'
            />
          </div>
          <div className='auth-form-group'>
            <label className='auth-label'>
              <Lock className='auth-icon' size={16} />
              {t('ph_password', 'Password')} *
            </label>
            <Input
              type='password'
              name='password'
              onChange={onFormChange}
              value={form.password}
              placeholder={t('signup_password_placeholder', 'Create a password')}
              required
              className='mb-0'
              inputClassName='auth-input-field'
            />
          </div>
          <div className='auth-form-group'>
            <label className='auth-label'>
              <Lock className='auth-icon' size={16} />
              {t('ph_repeat_password', 'Repeat Password')} *
            </label>
            <Input
              type='password'
              name='rpassword'
              onChange={onFormChange}
              value={form.rpassword}
              placeholder={t('signup_repeat_password_placeholder', 'Confirm your password')}
              errorText={passNoMatch ? t('error_password_no_match', "Passwords don't match") : ''}
              required
              className='mb-0'
              inputClassName='auth-input-field'
            />
          </div>
          <div className='auth-checkbox-wrapper'>
            <CheckBox
              id='policy_checkbox'
              name='policy'
              onChange={onFormChange}
              value={form.policy}
              label={
                <Trans i18nKey='checkbox_signup_policy'>
                  I agree to the{' '}
                  <Link to='/terms' target='_blank' className='auth-link'>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to='/policy' target='_blank' className='auth-link'>
                    Privacy Policy
                  </Link>
                </Trans>
              }
              required
            />
          </div>
          {error && <div className='auth-error'>{error}</div>}
          <div className='auth-submit-wrapper'>
            <Button
              type='submit'
              withLoading={fetch}
              disabled={fetch || passNoMatch || !form.policy}
              className='btn-primary btn-block auth-submit-btn'
            >
              {t('btn_register', 'Sign up')}
            </Button>
          </div>
        </form>
        <AuthConnect authType='signup' />
        <p className='auth-footer-text'>
          {t('signup_have_account', 'Already have an account?')}{' '}
          <a href={URLS.CONTACT} className='auth-link-bold'>
            {t('contact_us', 'Contact us')}
          </a>
        </p>
      </div>
    </>
  );
};

export default SignUp;
